import React, { useState, useEffect } from 'react';

import ReactDataGrid from 'react-data-grid';
import { Toolbar, Data } from 'react-data-grid-addons';

import ItemSelector from '../shared/ItemSelector';
import Title from '../shared/Title';

const selectors = Data.Selectors;

const handleFilterChange = filter => filters => {
  const newFilters = { ...filters };
  if (filter.filterTerm) {
    newFilters[filter.column.key] = filter;
  } else {
    delete newFilters[filter.column.key];
  }
  return newFilters;
};

const getRows = (rows, filters) => {
  return selectors.getRows({ rows, filters });
};

const sortRows = (initialRows, sortColumn, sortDirection) => rows => {
  const comparer = (a, b) => {
    if (sortDirection === 'ASC') {
      return a[sortColumn] > b[sortColumn] ? 1 : -1;
    } else if (sortDirection === 'DESC') {
      return a[sortColumn] < b[sortColumn] ? 1 : -1;
    }
  };
  return sortDirection === 'NONE'
    ? initialRows
    : [...rows].sort(comparer).map((row, i) => ({ ...row, rank: i + 1 }));
};

const parseRows = rows => {
  return rows
    ? rows.map(row => ({
        ...row.team,
        ...row.analysis,
        conference: row.team.conference.name,
        division: row.team.conference.division.name,
      }))
    : [];
};

const getValidFilterValues = (rows, columnId) => {
  return rows
    .map(row => row[columnId])
    .filter((item, i, a) => {
      return i === a.indexOf(item);
    });
};

const EmptyRowsView = () => {
  return (
    <div
      style={{ textAlign: 'center', backgroundColor: '#ddd', padding: '100px' }}
    >
      <h3>No data to show. Select a league/season/stage combination.</h3>
    </div>
  );
};

const StatsTable = ({
  impacts,
  columns,
  onStageChange,
  onLeagueChange,
  onSeasonChange,
  seasons,
  season,
  stages,
  stage,
  leagues,
  league,
}) => {
  const initialRows = parseRows(impacts);
  const [rows, setRows] = useState(initialRows);
  const [showScrollInfo, setShowScrollInfo] = useState(true);
  const [filters, setFilters] = useState({});
  const filteredRows = getRows(rows, filters);

  useEffect(() => {
    const updatedRows = getRows(parseRows(impacts), filters);
    setRows(updatedRows);
  }, [impacts, filters]);

  const toggleScrollInfo = () => {
    if (!showScrollInfo) return;
    setShowScrollInfo(false);
  };

  const filteredColumns = columns.filter(column =>
    league && ![1, 2, 22].includes(league.id)
      ? column.name !== 'Division'
      : true,
  );

  return (
    <div className="s-stats">
      <Title
        title="Stats"
        linkTo=""
        subtitle={'Filter and Sort Season Statistics'}
      />

      <div className="filter-container">
        <div className="filter filter--big">
          <div className="form-row">
            <div className="form-column">
              <div className="form-group">
                <ItemSelector
                  title="League"
                  items={leagues}
                  value={league}
                  empty="(All)"
                  onChange={onLeagueChange}
                />
              </div>
            </div>

            <div className="form-column">
              <div className="form-group">
                <ItemSelector
                  title="Season"
                  items={seasons}
                  value={season}
                  empty="(All)"
                  onChange={onSeasonChange}
                  disabledText="The league has no seasons"
                  disabled={!league}
                />
              </div>
            </div>

            <div className="form-column">
              <div className="form-group">
                <ItemSelector
                  title="Stage"
                  items={stages}
                  value={stage}
                  empty="(All)"
                  onChange={onStageChange}
                  disabledText="The season has no stages"
                  disabled={!league && !stage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {showScrollInfo && (
        <div className="s-stats__scroll-info">
          Scroll right for more columns... →
        </div>
      )}
      <ReactDataGrid
        columns={filteredColumns}
        rowGetter={i => filteredRows[i]}
        rowsCount={rows.length || 0}
        minHeight={640}
        toolbar={<Toolbar enableFilter={true} />}
        onAddFilter={filter => setFilters(handleFilterChange(filter))}
        onClearFilters={() => setFilters({})}
        getValidFilterValues={columnKey =>
          getValidFilterValues(rows, columnKey)
        }
        onGridSort={(sortColumn, sortDirection) =>
          setRows(sortRows(initialRows, sortColumn, sortDirection))
        }
        emptyRowsView={EmptyRowsView}
        onScroll={toggleScrollInfo}
        headerFiltersHeight={80}
      />
    </div>
  );
};

export default StatsTable;
